import { AUTH_URL } from 'src/configs';
import request from 'src/request';

import { ListMeta } from './adminUsers';

export type WalletOwner = {
  id: string;
  name?: { first?: string; last?: string } | null;
  email?: string;
  bxlend_id?: string;
};

export type WalletDecimal = string | number | { $numberDecimal?: string };

export type AdminWallet = {
  id: string;
  kind?: string;
  owner_type?: string;
  owner?: WalletOwner;
  currency_code?: string;
  currency_name?: string;
  address?: string | null;
  balance?: WalletDecimal;
  available_balance?: WalletDecimal;
  created_at?: string;
  updated_at?: string;
};

export type WhitelistUser = {
  id: string;
  name?: { first?: string; last?: string };
  email?: string;
  bxlend_id?: string;
};

export type WhitelistEntry = {
  id: string;
  address: string;
  network: string;
  type: 'WITHDRAWAL' | 'DEPOSIT' | string;
  status: 'ACTIVE' | 'DUE' | 'REVOKED' | string;
  source?: string;
  risk_level?: string;
  user?: WhitelistUser;
  first_used_at?: string;
  use_count?: number;
  last_used_at?: string;
  last_screened_at?: string;
  expires_at?: string;
  due_in_ms?: number;
  validity_days?: number;
  transaction_hash?: string;
  revoked_at?: string | null;
  revoke_reason?: string | null;
};

export type RescreenOutcome = 'RENEWED' | 'REVOKED' | 'FAILED';

export type RescreenResponse = {
  outcome: RescreenOutcome;
  expires_at?: string;
  risk_level?: string;
  reason?: string;
};

export type WalletSearchField = 'owner' | 'address' | 'currency_code';

const buildQuery = (params: Record<string, string | number | undefined | null>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.set(key, String(value));
  });
  const query = qs.toString();
  return query ? `?${query}` : '';
};

export const formatWalletOwnerName = (owner?: WalletOwner | WhitelistUser | null) => {
  const first = owner?.name?.first?.trim() || '';
  const last = owner?.name?.last?.trim() || '';
  return `${first} ${last}`.trim() || '—';
};

export const parseWalletAmount = (value?: WalletDecimal | null): string | null => {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object' && '$numberDecimal' in value) {
    const raw = value.$numberDecimal;
    return raw == null || raw === '' ? null : String(raw);
  }
  return null;
};

export const formatWalletBalance = (balance?: WalletDecimal | null, currencyCode?: string) => {
  const amount = parseWalletAmount(balance);
  if (amount == null) return '—';
  return currencyCode ? `${amount} ${currencyCode}` : amount;
};

export const formatNetworkLabel = (network?: string) => {
  if (!network) return '—';
  const key = network.toLowerCase();
  const short: Record<string, string> = {
    ethereum: 'ETH',
    bitcoin: 'BTC',
    tron: 'TRX',
    polygon: 'MATIC',
    solana: 'SOL',
    litecoin: 'LTC',
  };
  return short[key] || network.charAt(0).toUpperCase() + network.slice(1);
};

export const formatDueIn = (dueInMs?: number, status?: string) => {
  const normalized = String(status || '').toUpperCase();
  if (normalized === 'DUE' || dueInMs == null || dueInMs <= 0) {
    return { label: 'Due now', due: true };
  }
  const days = Math.ceil(dueInMs / (1000 * 60 * 60 * 24));
  return { label: `in ${days} day${days === 1 ? '' : 's'}`, due: false };
};

export const fetchAdminWallets = async (params: {
  page?: number;
  limit?: number;
  /** One of the indexed columns: created_at, id or currency_code. */
  sort?: string;
  order?: 'asc' | 'desc';
  /** Independent filters, ANDed by the API: one query param per field. */
  owner?: string;
  email?: string;
  address?: string;
  currency_code?: string;
  id?: string;
  /** Legacy single pair; still accepted and combined with the above. */
  field?: WalletSearchField;
  value?: string;
}) => {
  const { field, value, ...rest } = params;
  const response = await request.get(
    `${AUTH_URL}/v1/admin/wallets${buildQuery({
      ...rest,
      ...(field && value ? { field, value } : {}),
    })}`,
  );
  return {
    wallets: (response.data.wallets || []) as AdminWallet[],
    meta: response.data.meta as ListMeta,
  };
};

export const fetchComplianceWhitelist = async (params: {
  page?: number;
  limit?: number;
  sort?: 'whitelisted_at' | 'expires_at';
  order?: 'asc' | 'desc';
  chain?: string;
  status?: 'ACTIVE' | 'DUE' | 'REVOKED';
  type?: 'WITHDRAWAL' | 'DEPOSIT';
  search?: string;
}) => {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/compliance/whitelist${buildQuery(params)}`,
  );
  return {
    whitelist: (response.data.whitelist || []) as WhitelistEntry[],
    meta: response.data.meta as ListMeta,
  };
};

export const forceWhitelistRescreen = async (whitelistId: string) => {
  const response = await request.post(
    `${AUTH_URL}/v1/admin/compliance/whitelist/${whitelistId}/rescreen`,
    {},
  );
  return response.data as RescreenResponse;
};

/* ------------------------- Internal wallets ------------------------- */

/**
 * The platform's own custodial (treasury) wallets, held at Vaultody/BitGo —
 * distinct from the user wallets the main list shows.
 */
export type InternalWallet = {
  id: string;
  address?: string;
  currency_code?: string;
  balance?: WalletDecimal | null;
  platform?: string;
  platform_id?: string;
  vaultody_vault_account_id?: string;
  owner?: WalletOwner | null;
  created_at?: string;
  updated_at?: string;
};

export const fetchInternalWallets = async (params: { page?: number; limit?: number }) => {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/bitgo-wallets${buildQuery(params)}`,
  );
  return {
    wallets: (response.data.wallets || []) as InternalWallet[],
    meta: response.data.meta as ListMeta,
  };
};
